import { ChatSummaryControllerService } from '@api'
import { format } from 'date-fns/format'
import { formatISO } from 'date-fns/formatISO'
import { startOfDay } from 'date-fns/startOfDay'
import {
  attach,
  combine,
  createEffect,
  createEvent,
  createStore,
} from 'effector'
import { jsPDF } from 'jspdf'

import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { $fan, $fanId } from '@/models/chattingUI/chats/chats.ts'
import { $creatorId } from '@/models/creators.ts'
import { getLastDialogId, hideDialog, openDialog } from '@/models/dialogs.ts'
import { unescapeHtmlText } from '@/modules/chatting/ui/chat-window/fan-info/utils.ts'
import { handleError } from '@/utils/error'
import { delay } from '@/utils/misc.ts'

const TIMEZONE_OFFSET_REGEX = /(([+-]\d{2}:\d{2})|Z)$/

export const $nameToDisplay = $fan.map((fan) =>
  fan
    ? unescapeHtmlText(fan.displayName || fan.name).concat(
        fan.displayName ? ` (${unescapeHtmlText(fan.name)})` : '',
      )
    : '',
)

export const summarizeFx = attach({
  name: 'summarize chat',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (
    { date, fanId }: { fanId: number; date: Date },
    { creatorId },
  ) => ({
    creatorId: creatorId!,
    userId: fanId,
    start: formatISO(startOfDay(date)).replace(TIMEZONE_OFFSET_REGEX, ''),
  }),
  effect: createEffect(ChatSummaryControllerService.createSummary),
})

summarizeFx.failData.watch((error) => {
  handleError(error, 'The error occurred while summarizing')
})

export const getSummaryFx = attach({
  name: 'get chat summary',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (fanId: number, { creatorId }) => ({
    creatorId: creatorId!,
    fanId,
  }),
  effect: createEffect(
    async ({ creatorId, fanId }: { creatorId: number; fanId: number }) => {
      const { summary } = await ChatSummaryControllerService.getChatSummary({
        creatorId,
        userId: fanId,
      })

      return { summary, fanId }
    },
  ),
})

getSummaryFx.failData.watch((error) => {
  handleError(error, 'The error occurred while summarizing')
})

export const $summaryMap = createStore<Record<number, string>>(
  {},
  {
    name: 'chat summary map',
  },
).on(getSummaryFx.doneData, (store, { summary, fanId }) => ({
  ...store,
  [fanId]: summary!,
}))

export const $summary = combine($fanId, $summaryMap, (fanId, summaryMap) => {
  return summaryMap[fanId] ?? ''
})

export const setSummaryProgress = createEvent<number>(
  'set chat summary progress',
)
export const $summaryProgress = createStore<number>(0, {
  name: 'chat summary progress',
})
  .on(setSummaryProgress, (_, progress) => progress)
  .reset(summarizeFx)

export const trackSummaryProgressFx = attach({
  name: 'track chat summary progress',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (jobId: string, { creatorId }) => ({
    creatorId: creatorId!,
    jobId,
  }),
  effect: createEffect(
    async ({ creatorId, jobId }: { creatorId: number; jobId: string }) =>
      new Promise<void>((resolve, reject) => {
        const request = async () => {
          try {
            const { totalPercentage, state } =
              (await ChatSummaryControllerService.getStatus({
                creatorId,
                jobId,
              })) ?? {}

            const isSucceed = state === 'DONE'
            const isFinalizing = state === 'FINALIZING'
            const isFailed = !state || state === 'FAILED'
            const progress =
              totalPercentage === 0 && (isSucceed || isFinalizing)
                ? 100
                : Number(totalPercentage)

            if (isSucceed) {
              setSummaryProgress(100)
              resolve()

              return
            }

            if (isFailed) {
              reject(new Error('Summarizing failed'))

              return
            }

            setSummaryProgress(progress)
            // recursion
            await delay(2000)
            request()
          } catch (error) {
            reject(new Error('Summarizing failed'))
          }
        }

        request()
      }),
  ),
})

trackSummaryProgressFx.failData.watch((error) => {
  handleError(error, 'The error occurred while summarizing')
})

export const getChatSummaryReportFx = attach({
  name: 'get chat summary report',
  source: {
    fanId: $fanId,
  },
  mapParams: (date: Date, { fanId }) => ({
    fanId,
    date,
  }),
  effect: createEffect(
    async ({ date, fanId }: { fanId: number; date: Date }) => {
      openDialog({
        type: DIALOG_TYPE.CHAT_SUMMARIZE_PROGRESS,
      })
      const progressDialogId = getLastDialogId()

      try {
        // pass fanId as arg, because store value maybe be change during execution
        const progressId = await summarizeFx({ fanId, date })

        await trackSummaryProgressFx(progressId)

        await getSummaryFx(fanId)

        hideDialog(progressDialogId)

        // if we still at the same fan - let display dialog, otherwise only fill model
        // eslint-disable-next-line effector/no-getState
        if ($fanId.getState() === fanId) {
          openDialog({
            type: DIALOG_TYPE.CHAT_SUMMARY,
            date: format(
              new Date(
                formatISO(startOfDay(date)).replace(TIMEZONE_OFFSET_REGEX, ''),
              ),
              'MMM d, yyyy',
            ),
          })
        }
      } catch (error) {
        hideDialog(progressDialogId)
      }
    },
  ),
})

export const exportSummaryFx = attach({
  name: 'export chat summary with pdf',
  source: {
    nameToDisplay: $nameToDisplay,
    summary: $summary,
  },
  mapParams: (formattedStartDate: string, { nameToDisplay, summary }) => ({
    nameToDisplay,
    formattedStartDate,
    summary: summary!,
  }),
  effect: createEffect(
    async ({
      nameToDisplay,
      formattedStartDate,
      summary,
    }: {
      nameToDisplay: string
      formattedStartDate: string
      summary: string
    }) => {
      const doc = new jsPDF()

      // Add title
      doc.setFontSize(18)
      doc.text('Chat Summary', 10, 10)

      const content = [
        `User: ${nameToDisplay}`,
        `From: ${formattedStartDate}`,
        'Summarized Chat:',
        doc.splitTextToSize(summary!, 280),
      ].filter((line): line is string => !!line)

      doc.setFontSize(12)

      // Add content
      content.forEach((line, index) => {
        doc.text(line, 10, 20 + index * 10)
      })

      // Save the PDF
      doc.save('chat-summary.pdf')
    },
  ),
})
